import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

const VerifyMagicLink = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { verifyMagicLink } = useAuth();
  const [status, setStatus] = useState("verifying"); // verifying, success, error
  const [error, setError] = useState(null);
  const hasVerified = useRef(false);

  useEffect(() => {
    const verify = async () => {
      // Prevent duplicate verification attempts (React.StrictMode causes double-invocation)
      if (hasVerified.current) {
        return;
      }
      hasVerified.current = true;

      const token = searchParams.get("token");

      if (!token) {
        setStatus("error");
        setError("No token provided");
        return;
      }

      try {
        await verifyMagicLink(token);
        setStatus("success");

        // Redirect to dashboard after success
        setTimeout(() => {
          navigate("/");
        }, 1500);
      } catch (err) {
        console.error("Verification failed:", err);
        setStatus("error");
        setError(
          err.response?.data?.detail || "Invalid or expired link. Please try again."
        );
      }
    };

    verify();
  }, [searchParams, verifyMagicLink, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
      <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8">
        <div className="text-center">
          {status === "verifying" && (
            <>
              <div className="mx-auto flex items-center justify-center h-12 w-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
                Verifying...
              </h3>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                Please wait while we sign you in
              </p>
            </>
          )}

          {status === "success" && (
            <>
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 dark:bg-green-900">
                <svg
                  className="h-6 w-6 text-green-600 dark:text-green-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
                Success!
              </h3>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                You're now signed in. Redirecting...
              </p>
            </>
          )}

          {status === "error" && (
            <>
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900">
                <svg
                  className="h-6 w-6 text-red-600 dark:text-red-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
                Verification failed
              </h3>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                {error}
              </p>
              <button
                onClick={() => navigate("/login")}
                className="mt-6 text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-500"
              >
                Back to login
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyMagicLink;
